/**
 * Servicio de emisión: construcción de payloads y llamadas CRM/API.
 * Sin estado ni refs; recibe datos planos.
 */

import axios from 'axios';
import {
  executeForCRM,
  executeForCRUD,
  type IRequestParams,
  type ExecuteForCRUDResponse,
} from '@/services/frontBack';
import {
  mapFormaPago,
  mapPaquete,
  getNombreCia,
  calcularEdad,
  getIdSexo,
  buildConcepto,
} from '@/utils/emissionMappers';
import type {
  EmissionFormData,
  EmissionQuoteContext,
  EmissionCoberturaContext,
  Documento,
} from '@/types/emission.types';
import type { WidgetAgentDataForRequest } from '@/utils/widgetAgent';
import {
  getInsurerDocumentDownloadUrl,
  getInsurerEmitUrl,
} from '@/config/api';

const ARTICULO_492_DECLARACION = 'Declaro que recabé la información requerida por el Artículo 492 de la Ley de Instituciones de Seguros y de Fianzas para esta emisión.';

export interface CotizacionesEmisionesInsertOptions {
  idCotizacion: number;
  idAgente: number;
  idPoliza: number;
  emitida: number;
  formaPago: string;
  paquete: string;
  selectedPlanType: string;
  selectedPaymentMethod: string;
  vehicleDescription: string;
}

/**
 * Construye el payload para Update de cotizaciones_autos_detalle.
 */
export function buildUpdateQuoteParams(
  quote: EmissionQuoteContext,
  formData: EmissionFormData,
  _cobertura: EmissionCoberturaContext | undefined,
  agentData: WidgetAgentDataForRequest,
  idCotizacion: number,
  idCotizacionAutosDetalle: number,
  selectedPaymentMethod: string,
  selectedPlanType: string
): IRequestParams {
  const nombreCompleto = `${formData.nombres} ${formData.paterno} ${formData.materno}`.trim();
  const formaPago = mapFormaPago(selectedPaymentMethod);
  const paquete = mapPaquete(selectedPlanType);
  const cotizacionData = quote.cotizacionData ?? {};

  const params: IRequestParams = {
    ClassName: 'cotizaciones_autos_detalle',
    Action: 'Update',
    db: 'Prometeo',
    UsuarioAdd: agentData.Id_Usuario,
    UsuarioUmod: agentData.Id_Usuario,
    UsuarioDel: agentData.Id_Usuario,
    Id_Usuario_Modificacion: agentData.Id_Usuario_Modificacion,
    Id_Cotizacion_Autos_Detalle: Number(idCotizacionAutosDetalle),
    Id_Cotizacion: idCotizacion,
    Modelo: quote.year ? String(quote.year) : '',
    Marca: quote.brand?.id ?? '',
    Tipo: quote.type ?? '',
    Clave_Qualitas: quote.amis ?? '',
    Clave_Ana: '0',
    Id_Aut_Colectividad: 1,
    Cias: '2,3',
    Paquetes: paquete,
    Formas_Pago: formaPago,
    Descuento_Ana: Number(cotizacionData.descuentoAna ?? 40),
    Descuento_Qualitas: Number(cotizacionData.descuentoQualitas ?? 45),
    Deducible_DM: Number(cotizacionData.deducibleDM ?? 5),
    Deducible_Robo: Number(cotizacionData.deducibleRobo ?? 10),
    Nombre_Prospecto: (nombreCompleto || quote.personalData?.fullName) ?? '',
    Codigo_Postal: (formData.cp || quote.personalData?.postalCode) ?? '',
    Id_Sexo: getIdSexo(quote.personalData?.gender),
    Email_Prospecto: (formData.email || quote.personalData?.email) ?? '',
    Telefono_Prospecto: (formData.telefonos || quote.personalData?.whatsapp) ?? '',
    Edad: calcularEdad(formData.fechaNacimiento || quote.personalData?.birthDate),
    Id_Estado: formData.idEstado ?? quote.personalData?.idEstado,
    Id_Ciudad: formData.idCiudad ?? quote.personalData?.idCiudad,
    Id_Municipio: formData.idMunicipio ?? quote.personalData?.idMunicipio,
    Para: '',
    Subject: '',
    Body: '',
    Descripcion: '',
    Id_Cotizaciones_Campania: 3,
    Consentimiento_Privacidad: quote.personalData?.privacyConsent ? 1 : 0,
  };

  if (agentData.Id_Persona !== null) {
    params.Id_Persona_Vendedor = agentData.Id_Persona;
  }

  const clean: IRequestParams = { ...params };
  Object.keys(clean).forEach((key) => {
    if (clean[key as keyof IRequestParams] === undefined) {
      delete clean[key as keyof IRequestParams];
    }
  });
  return clean;
}

/**
 * Construye el payload para Insert en Cotizaciones_Emisiones (SP p_Cotizaciones_Emisiones_Insert).
 * Para "Me interesa" usar idPoliza: 0, emitida: 0.
 */
export function buildCotizacionesEmisionesInsertParams(
  quote: EmissionQuoteContext,
  formData: EmissionFormData,
  cobertura: EmissionCoberturaContext | undefined,
  agentData: WidgetAgentDataForRequest,
  options: CotizacionesEmisionesInsertOptions
): IRequestParams {
  const codigoCiudad = formData.idCiudad != null
    ? String(formData.idCiudad)
    : (quote.personalData?.idCiudad != null ? String(quote.personalData.idCiudad) : '');

  return {
    ClassName: 'Cotizaciones_Emisiones',
    Action: 'Insert',
    Id_Cotizacion: options.idCotizacion,
    Id_Agente: options.idAgente,
    Id_Poliza: options.idPoliza,
    Poliza_Numero: options.idPoliza ? undefined : null,
    Emitida: options.emitida,
    Id_Cotizacion_Propuesta: cobertura?.Id_Cotizacion_Propuesta != null ? Number(cobertura.Id_Cotizacion_Propuesta) : 0,
    Cia: cobertura?.Id_Cia != null ? String(cobertura.Id_Cia) : '',
    Nombre_Cia: cobertura?.Id_Cia != null ? getNombreCia(cobertura.Id_Cia) : '',
    Id_Productos_Paquete: cobertura?.Id_Productos_Paquete != null ? String(cobertura.Id_Productos_Paquete) : '',
    Paquete: options.paquete,
    Nombre_Paquete: options.selectedPlanType,
    Id_Forma_Pago: options.formaPago,
    Forma_Pago: options.formaPago,
    Nombre_Forma_Pago: options.selectedPaymentMethod,
    Prima_Neta: cobertura?.PrimaNeta != null ? String(cobertura.PrimaNeta) : '',
    Prima_Total: cobertura?.PrimaTotal != null ? String(cobertura.PrimaTotal) : '',
    Comision: cobertura?.Comision != null ? String(cobertura.Comision) : '',
    Xml_Request: cobertura?.Request ?? '',
    Xml_Response: cobertura?.Response ?? '',
    Paterno: formData.paterno,
    Materno: formData.materno || '',
    Nombres: formData.nombres,
    RFC: formData.rfc,
    Fecha_Nacimiento: formData.fechaNacimiento,
    Telefonos: formData.telefonos,
    Email: formData.email,
    Calle: formData.calle,
    Numero_Exterior: formData.numeroExterior,
    Numero_Interior: formData.numeroInterior || 'S/N',
    Colonia: formData.colonia,
    Municipio: formData.municipio,
    Estado: formData.estado,
    CP: formData.cp,
    Codigo_Estado: formData.idEstado != null ? String(formData.idEstado) : null,
    Codigo_Municipio: formData.idMunicipio != null ? String(formData.idMunicipio) : null,
    Codigo_Colonia: null,
    Codigo_Ciudad: codigoCiudad || null,
    Vehiculo_Amis: quote.amis ?? '',
    Vehiculo_Marca_Nombre: quote.brand?.name ?? '',
    Vehiculo_Marca_Clave: quote.brand?.id ?? '',
    Vehiculo_Modelo: quote.year != null ? String(quote.year) : '',
    Vehiculo_Tipo: quote.type ?? '',
    Vehiculo_Descripcion: options.vehicleDescription,
    Vehiculo_Serie: formData.numeroSerie.trim(),
    Vehiculo_Motor: formData.numeroMotor.trim(),
    Vehiculo_Placas: formData.placas.trim(),
    Articulo492_Aceptado: formData.articulo492Aceptado,
    Articulo492_Fecha_Aceptacion: formData.articulo492FechaAceptacion || null,
    Articulo492_Declaracion: ARTICULO_492_DECLARACION,
    Articulo492_Documentos_Cargados: formData.articulo492DocumentosCargados,
    Articulo492_Documentos_Metadata: formData.articulo492DocumentosMetadata,
    Vigencia_Inicio: quote.cotizacionData?.inicioVigencia ?? '',
    Vigencia_Fin: quote.cotizacionData?.finVigencia ?? '',
    Url_Descarga_Poliza: options.idPoliza ? undefined : null,
    Url_Descarga_Recibos: null,
    Url_Descarga_Textos: null,
    Pagada: null,
    UsuarioAdd: agentData.UsuarioAdd,
    UsuarioUmod: agentData.UsuarioUmod,
    UsuarioDel: agentData.UsuarioDel,
    Id_Usuario_Modificacion: agentData.Id_Usuario_Modificacion,
  };
}

export async function updateCotizacionAutosDetalle(
  params: IRequestParams
): Promise<ExecuteForCRUDResponse> {
  return executeForCRUD(params);
}

export async function insertCotizacionesEmisiones(
  params: IRequestParams
): Promise<ExecuteForCRUDResponse> {
  return executeForCRM(params);
}

export async function getCotizacionesEmisiones(
  idCotizacion: number
): Promise<ExecuteForCRUDResponse> {
  return executeForCRM({
    ClassName: 'Cotizaciones_Emisiones',
    Action: 'Get',
    Id_Cotizacion: idCotizacion,
  });
}

export function buildPolizasInsertParams(
  quote: EmissionQuoteContext,
  agentData: WidgetAgentDataForRequest,
  idCotizacion: number,
  idAgente: number
): IRequestParams {
  const concepto = buildConcepto(quote);
  return {
    ClassName: 'Polizas',
    Action: 'Insert',
    action_path: '/Nf_Data/ExecuteForCRUD',
    Id_Oficina: agentData.Id_Oficina,
    Id_Usuario: agentData.Id_Usuario,
    Id_Usuario_Add: agentData.Id_Usuario,
    UsuarioAdd: agentData.UsuarioAdd,
    UsuarioDel: agentData.UsuarioDel,
    user: agentData.user,
    Id_Usuario_Modificacion: agentData.Id_Usuario_Modificacion,
    UsuarioUMod: agentData.UsuarioUMod,
    Registro_Activo: 1,
    Id_Moneda: 2,
    Id_Ramo: 3,
    Id_Agente: idAgente,
    Concepto: concepto || 'Vehículo',
    Id_Cotizacion: idCotizacion,
  };
}

export async function insertPolizas(params: IRequestParams): Promise<ExecuteForCRUDResponse> {
  return executeForCRM(params);
}

export async function getDocumentosXSolicitud(idSolicitud: number): Promise<Documento[]> {
  const response = await executeForCRM({
    ClassName: 'Documentos_X_Solicitud',
    Action: 'Get',
    Id_Solicitud: idSolicitud,
  });

  if (!response.success || !response.data || !Array.isArray(response.data)) {
    return [];
  }

  return response.data
    .filter((doc: unknown) => {
      const d = doc as Record<string, unknown>;
      return d.Guid_Documento && d.Id_Solicitud === idSolicitud;
    })
    .map((doc: unknown) => {
      const d = doc as Record<string, unknown>;
      return {
        Guid_Documento: String(d.Guid_Documento ?? ''),
        Nombre: d.Nombre ? String(d.Nombre) : undefined,
        Tipo: d.Tipo ? String(d.Tipo) : undefined,
        Id_Solicitud: d.Id_Solicitud ? Number(d.Id_Solicitud) : undefined,
      } as Documento;
    });
}

export function getDocumentDownloadUrl(guidDocumento: string): string {
  return getInsurerDocumentDownloadUrl(guidDocumento);
}

export async function emitirQualitas(
  idPoliza: number
): Promise<{ success: boolean; data?: unknown }> {
  try {
    const body = new URLSearchParams();
    body.append('Id_Poliza', String(idPoliza));
    const response = await axios.post(
      getInsurerEmitUrl('QUALITAS'),
      body.toString(),
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      },
    );
    const isSuccess = response.data?.success === true;
    if (isSuccess) return { success: true, data: response.data };
    return { success: false, data: response.data };
  } catch {
    return { success: false };
  }
}

export async function emitirAna(
  idPoliza: number
): Promise<{ success: boolean; data?: unknown }> {
  try {
    const body = new URLSearchParams();
    body.append('Id_Poliza', String(idPoliza));
    const response = await axios.post(
      getInsurerEmitUrl('ANA'),
      body.toString(),
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      },
    );
    const isSuccess = response.data?.success === true;
    if (isSuccess) return { success: true, data: response.data };
    return { success: false, data: response.data };
  } catch {
    return { success: false };
  }
}

/**
 * Extrae Id_Poliza e Id_Solicitud de la respuesta de Get Cotizaciones_Emisiones o Insert Polizas.
 */
export function parseIdPolizaFromResponse(response: ExecuteForCRUDResponse): {
  idPoliza: number | null;
  idSolicitud: number | null;
} {
  let idPoliza: number | null = null;
  let idSolicitud: number | null = null;
  const source = response.data?.length ? response.data : response.dataTable;
  if (source && Array.isArray(source) && source.length > 0) {
    const first = source[0] as Record<string, unknown>;
    idPoliza = first.Id_Poliza != null ? Number(first.Id_Poliza) : null;
    idSolicitud = first.Id_Solicitud != null ? Number(first.Id_Solicitud) : null;
  }
  return { idPoliza, idSolicitud };
}
